import { useId, useState, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import type { TradingSignal } from '../../types'
import { SIGNALS_GLOSSARY as GLOSSARY } from '../../config/glossary'
import CoinIcon from '../CoinIcon'
import RationaleText, { stripEvClause } from '../RationaleText'
import Tooltip from '../Tooltip'
import { formatPrice } from '../../utils/marketPrice'
import { formatPnlPct } from '../../utils/pnl'
import { parseApiDate, signalExpiry } from '../../utils/signalExpiry'
import { exitReason, rewardRisk, signalPnlPct } from '../../utils/signalPnl'
import { callPath } from '../../routes'
import CallLink from './CallLink'
import PricePathBar from './PricePathBar'
import {
  DirectionChip,
  EXPIRY_STYLES,
  ExcludedChip,
  ExitReasonChip,
  isOpenSignal,
  pnlText,
} from './signalChips'
import { displaySymbol, isRenamedSymbol } from '../../utils/displaySymbol'

const usd = (v: number) => `$${formatPrice(v)}`

/** backend leverage_calculator.LEVERAGE_OFF_RATIONALE: the card's one leverage line while suggestions are off. */
export const LEVERAGE_OFF_LINE = 'No leverage suggested while the signal engine is under evaluation.'

/** "Oct 17, 7:01 PM": the expiry in the viewer's time zone, no seconds. */
const formatExpiry = (d: Date) =>
  d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

/** First sentence of the rationale: the regime reason for the leverage call. */
const regimeReason = (rationale?: string | null) => {
  if (!rationale) return 'The model did not recommend leverage for this setup.'
  // Split on ". " so decimals like 0.0713 stay intact (no lookbehind: older
  // Safari cannot parse it, which would break the whole bundle).
  const first = rationale.split(/\.\s+/)[0].trim()
  return first.endsWith('.') ? first : `${first}.`
}

const Level = ({ label, value, sub, subClass = 'text-gray-400', hint }: { label: string; value: string; sub?: ReactNode; subClass?: string; hint?: string }) => (
  <div className="rounded-md border border-terminal-border bg-terminal-bg px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-gray-500">
      {hint ? <Tooltip content={hint} className="uppercase">{label}</Tooltip> : label}
    </p>
    <p className="font-mono text-base font-semibold tabular-nums text-gray-50">{value}</p>
    {sub != null && <p className={`font-mono text-xs tabular-nums ${subClass}`}>{sub}</p>}
  </div>
)

const TrustChip = ({ children, hint }: { children: string; hint?: string }) => (
  <span className="rounded-full border border-terminal-border bg-white/5 px-2.5 py-1 text-xs text-gray-300">
    {hint ? <Tooltip content={hint}>{children}</Tooltip> : children}
  </span>
)

interface SignalDetailCardProps {
  signal: TradingSignal
  /** Latest price for an open signal. */
  price: number | null
  now: number
  /** Present only for users who pass the live-trading gate. */
  onTrade?: () => void
  /**
   * When the desktop card is pinned and the list has scrolled, the leverage
   * box collapses away so the card shrinks to header, price path, levels,
   * exit line and footer. Off on the mobile slide-over, which is always full.
   */
  condensed?: boolean
  /** Show the call's permalink and a copy-link button (off on the public page itself). */
  permalink?: boolean
  /**
   * Name the stored symbol beside a renamed one ("recorded as MATIC/USDT"), for
   * the public call page, where the record and its fingerprint use the stored value.
   */
  showRecordedSymbol?: boolean
  /**
   * A creator's call, not the engine's: it has no model confidence, phase
   * scores, regime or leverage advice, so none are shown. Defaults to the
   * call's own mode ('creator' for a locked call).
   */
  creatorCall?: boolean
  /**
   * Leave the Current box out of an open signal when ``price`` is null, rather
   * than showing "no live price" (the public call page, which has no price
   * until its poll returns one).
   */
  hideMissingPrice?: boolean
}

const copyCallLink = async (id: number) => {
  const url = `${window.location.origin}${callPath(id)}`
  try {
    await navigator.clipboard.writeText(url)
    toast.success('Link copied')
  } catch {
    toast.error(`Could not copy. The link is ${url}`)
  }
}

/**
 * The one expanded signal: header, price path, levels, exit line and
 * leverage, then the trust chips and "Why this signal?". P&L is at 1×.
 */
const SignalDetailCard = ({
  signal,
  price,
  now,
  onTrade,
  condensed = false,
  permalink = false,
  showRecordedSymbol = false,
  creatorCall = signal.trading_mode === 'creator',
  hideMissingPrice = false,
}: SignalDetailCardProps) => {
  const [whyOpen, setWhyOpen] = useState(false)
  const whyId = useId()
  const open = isOpenSignal(signal)
  const reason = open ? null : exitReason(signal)
  const expiry = open ? signalExpiry(signal, now) : null
  // When the signal stops being held if neither level is crossed first.
  const parsedExpiry = signal.expires_at ? parseApiDate(signal.expires_at) : null
  const expiresAt = parsedExpiry && !Number.isNaN(parsedExpiry.getTime()) ? parsedExpiry : null
  // Closed (target, stop or expiry at market): the exit price and recorded P&L.
  // Cancelled: no exit, no result.
  const mark = open ? price : signal.status === 'closed' ? signal.exit_price ?? null : null
  const pnl = open ? signalPnlPct(signal, price) : signal.status === 'closed' ? signal.profit_loss_percent ?? null : null
  const targetPct = signalPnlPct(signal, signal.take_profit)
  const stopPct = signalPnlPct(signal, signal.stop_loss)
  const rr = rewardRisk(signal)
  const hideCurrent = hideMissingPrice && open && price == null
  // Off (the default): one line for every signal, open or closed, old ones
  // included, whatever leverage they stored.
  const suggestionsOn = signal.leverage_suggestions_enabled === true
  const leveraged = suggestionsOn && (signal.recommended_leverage ?? 1) > 1
  const risk = signal.recommended_risk_pct ?? (signal.trading_mode === 'growth' ? 0.025 : 0.01)
  // Stored rationales from before EV left them still quote it; never show it.
  const rationale = !suggestionsOn
    ? LEVERAGE_OFF_LINE
    : signal.leverage_rationale
    ? stripEvClause(signal.leverage_rationale)
    : null
  const regime = signal.market_regime ? signal.market_regime.charAt(0).toUpperCase() + signal.market_regime.slice(1) : null

  return (
    <article
      className="rounded-xl border border-terminal-border bg-terminal-surface p-4 md:p-6"
      data-testid="signal-card"
      data-status={signal.status}
      data-signal-id={signal.id}
    >
      {/* (a) header */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <CoinIcon symbol={signal.symbol} size={32} />
          <h2 className="text-2xl font-bold text-gray-50">{displaySymbol(signal.symbol)}</h2>
          {showRecordedSymbol && isRenamedSymbol(signal.symbol) && (
            <span className="text-xs text-gray-400" data-testid="recorded-symbol">
              (recorded as {signal.symbol})
            </span>
          )}
          <DirectionChip direction={signal.signal_type} />
          <span className="rounded bg-white/5 px-1.5 py-0.5 text-xs text-gray-300">{signal.timeframe}</span>
          {regime && (
            <span className="text-xs text-gray-400">
              <Tooltip content={GLOSSARY.regimeTag}>{regime}</Tooltip>
            </span>
          )}
          {signal.trading_mode === 'growth' && (
            <span className="rounded bg-green-500/10 px-1.5 py-0.5 text-xs text-green-300">
              <Tooltip content={GLOSSARY.tradingMode}>Growth</Tooltip>
            </span>
          )}
        </div>
        {expiry ? (
          <span
            data-testid="signal-expiry"
            data-state={expiry.state}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${EXPIRY_STYLES[expiry.state]}`}
          >
            {expiry.state === 'soon' && <span aria-hidden="true">⏳</span>}
            <Tooltip content={`${GLOSSARY.expiry} Expiry: ${formatExpiry(expiry.expiresAt)}.`}>{expiry.label}</Tooltip>
          </span>
        ) : (
          reason && <ExitReasonChip reason={reason} />
        )}
      </header>

      {/* (b) price path */}
      <div className="mt-5">
        <PricePathBar signal={signal} current={mark} />
      </div>

      {/* (c) levels */}
      <div className={`mt-5 grid grid-cols-2 gap-2 ${hideCurrent ? 'sm:grid-cols-4' : 'sm:grid-cols-5'}`}>
        <Level label="Entry" value={usd(signal.entry_price)} />
        {!hideCurrent && (
          <Level
            label={open ? 'Current' : signal.status === 'closed' ? 'Exit' : 'Result'}
            value={mark != null ? usd(mark) : '—'}
            sub={pnl != null ? formatPnlPct(pnl) : open ? 'no live price' : 'no result'}
            subClass={pnlText(pnl)}
            hint={open ? GLOSSARY.sinceEntry : undefined}
          />
        )}
        <Level label="Target" value={usd(signal.take_profit)} sub={targetPct != null ? `if hit ${formatPnlPct(targetPct)}` : undefined} subClass="text-green-400" />
        <Level label="Stop" value={usd(signal.stop_loss)} sub={stopPct != null ? `if hit ${formatPnlPct(stopPct).replace('-', '−')}` : undefined} subClass="text-red-400" />
        <Level
          label="Reward:risk"
          value={rr != null ? `${rr.toFixed(2)} : 1` : '—'}
          hint="Distance to the target divided by distance to the stop. Above 1 means the target is further away than the stop."
        />
      </div>

      {/* (d) the exit rule the record scores (backend services/signal_exit.decide_exit):
          the whole position is held until the first of target, stop or expiry. */}
      <div className="mt-3 space-y-2" data-testid="exit-line">
        <p className="text-xs text-gray-400" data-testid="exit-rule">
          <Tooltip content={GLOSSARY.exitManagement}>
            {open
              ? `Held in full until the target, the stop or expiry (closes at market${
                  expiresAt ? `, ${formatExpiry(expiresAt)}` : ''
                }) — whichever comes first.`
              : 'Held in full until the target, the stop or expiry — whichever came first.'}
          </Tooltip>
        </p>
        {/* A closed/expired signal has no exit left to manage: show the levels
            as the record they are, never as a next action to take now. */}
        {!open && (
          <p className="text-xs text-gray-400" data-testid="exit-historical">
            This signal has closed — shown as history, not a live instruction.
          </p>
        )}
        {signal.excluded_from_record && (
          <div
            className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            data-testid="exclusion-note"
          >
            <p className="flex items-center gap-2 font-semibold">
              <ExcludedChip note={signal.exclusion_note} /> Excluded from the track record
            </p>
            <p className="mt-1">
              {signal.exclusion_note ?? 'This signal is kept here but does not count in any record figure.'} Its
              recorded result is shown unchanged.
            </p>
          </div>
        )}
      </div>

      {/* (e) leverage -- collapses when the pinned card condenses; none on a creator's call */}
      {!creatorCall && (
        <div
          className={`overflow-hidden transition-all duration-300 ${
            condensed ? 'mt-0 max-h-0 opacity-0' : 'mt-4 max-h-[640px] opacity-100'
          }`}
          aria-hidden={condensed || undefined}
          data-testid="exit-leverage"
        >
          <section
            className={`rounded-lg border p-3 ${leveraged ? 'border-green-500/40 bg-green-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}
            aria-label="Leverage"
            data-testid="leverage-box"
            data-leveraged={leveraged}
          >
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className={`text-sm font-semibold ${leveraged ? 'text-green-200' : 'text-amber-200'}`}>
                {leveraged ? (
                  'Leverage'
                ) : !suggestionsOn ? (
                  <Tooltip content={GLOSSARY.leverageOff}>Leverage</Tooltip>
                ) : (
                  <Tooltip content={GLOSSARY.leverageNotRecommended}>Leverage: not recommended (1×)</Tooltip>
                )}
              </h3>
              <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-gray-300">P&amp;L shown at 1×</span>
            </div>
            {leveraged ? (
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-400">Recommended</dt>
                  <dd className="font-mono font-semibold text-green-200">{signal.recommended_leverage!.toFixed(1)}×</dd>
                </div>
                {signal.max_safe_leverage != null && (
                  <div className="flex justify-between">
                    <dt className="text-gray-400">
                      <Tooltip content={GLOSSARY.leverageCeiling}>Max safe</Tooltip>
                    </dt>
                    <dd className="font-mono text-gray-200">{signal.max_safe_leverage.toFixed(0)}×</dd>
                  </div>
                )}
                {signal.liquidation_price != null && (
                  <div className="flex justify-between">
                    <dt className="text-gray-400">Liquidation price</dt>
                    <dd className="font-mono text-red-300">{usd(signal.liquidation_price)}</dd>
                  </div>
                )}
                {signal.sl_before_liquidation != null && (
                  <p className={signal.sl_before_liquidation ? 'text-green-300' : 'font-semibold text-red-300'}>
                    {signal.sl_before_liquidation ? 'Stop triggers before liquidation ✓' : '⚠ Liquidation comes before the stop — reduce size'}
                  </p>
                )}
                {signal.estimated_funding_cost_pct != null && (
                  <div className="flex justify-between">
                    <dt className="text-gray-400">Est. funding</dt>
                    <dd className="font-mono text-gray-200">{signal.estimated_funding_cost_pct.toFixed(2)}%</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-amber-100/90" data-testid="leverage-reason">
                {suggestionsOn ? regimeReason(rationale) : LEVERAGE_OFF_LINE}
              </p>
            )}
          </section>
        </div>
      )}

      {/* (f) footer */}
      <footer className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-terminal-border pt-4">
        <div className="flex flex-wrap gap-2" data-testid="trust-chips">
          {!creatorCall && (
            <>
              <TrustChip hint={GLOSSARY.confidence}>{`Confidence ${(signal.overall_confidence * 100).toFixed(0)}%`}</TrustChip>
              <TrustChip hint={GLOSSARY.riskPct}>{`Risk ${+(risk * 100).toFixed(2)}%`}</TrustChip>
            </>
          )}
          {signal.excluded_from_record ? (
            <TrustChip hint="Kept and listed with the reason, but not counted in any record figure. Wins and losses are excluded by the same rule.">
              Excluded, still listed
            </TrustChip>
          ) : (
            <TrustChip hint="Every closed signal counts in the record at its full loss — none are hidden or removed.">Losses shown in full</TrustChip>
          )}
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {permalink && (
            <span className="flex items-center gap-3 self-center text-sm" data-testid="card-permalink">
              <CallLink id={signal.id} />
              <button
                type="button"
                onClick={() => copyCallLink(signal.id)}
                className="text-xs text-gray-400 hover:text-gray-200"
                data-testid="copy-call-link"
              >
                Copy link
              </button>
            </span>
          )}
          {!creatorCall && (
            <button
              type="button"
              onClick={() => setWhyOpen((o) => !o)}
              aria-expanded={whyOpen}
              aria-controls={whyId}
              className="rounded-md border border-terminal-border px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/5"
            >
              Why this signal? {whyOpen ? '▲' : '▼'}
            </button>
          )}
          {onTrade && open && signal.signal_type === 'long' && (
            <button
              type="button"
              onClick={onTrade}
              className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-terminal-bg hover:bg-accent-hover"
              data-testid="trade-signal"
            >
              Trade this signal
            </button>
          )}
          {/* Kraken Spot can't short, so a SHORT gets a note, not a button that could only fail. */}
          {onTrade && open && signal.signal_type === 'short' && (
            <span className="self-center px-2 text-xs text-gray-500" data-testid="spot-only-note">
              Spot only — no shorts
            </span>
          )}
        </div>
      </footer>

      {whyOpen && !creatorCall && (
        <div id={whyId} className="mt-4 space-y-4 rounded-lg border border-terminal-border bg-terminal-bg p-4 text-sm" data-testid="why-panel">
          {signal.phase1_score != null && signal.phase2_score != null && signal.phase3_score != null && (
            <div className="grid grid-cols-3 gap-2 text-center">
              {(
                [
                  ['Phase 1', signal.phase1_score, GLOSSARY.phase1],
                  ['Phase 2', signal.phase2_score, GLOSSARY.phase2],
                  ['Phase 3', signal.phase3_score, GLOSSARY.phase3],
                ] as const
              ).map(([name, score, hint]) => (
                <div key={name} className="rounded-md border border-terminal-border px-2 py-2">
                  <p className="text-xs text-gray-400">
                    <Tooltip content={hint}>{name}</Tooltip>
                  </p>
                  <p className="font-mono font-semibold text-gray-100">{(score * 100).toFixed(0)}%</p>
                </div>
              ))}
            </div>
          )}
          {regime && (
            <p className="text-gray-300">
              <span className="font-semibold text-gray-100">
                <Tooltip content={GLOSSARY.regimeConfidence}>Regime</Tooltip>:
              </span>{' '}
              {GLOSSARY.regimeWithConfidence(
                regime,
                signal.regime_confidence != null ? `Its ${(signal.regime_confidence * 100).toFixed(0)}% confidence` : 'The confidence'
              )}
            </p>
          )}
          {rationale && (
            <p className="leading-relaxed text-gray-300">
              <span className="font-semibold text-gray-100">Leverage reasoning: </span>
              <RationaleText text={rationale} />
            </p>
          )}
        </div>
      )}
    </article>
  )
}

export default SignalDetailCard
